/**
 * Browser-side Web Push manager.
 *
 *   await ensurePushPermissionAndSubscribe();
 *
 * Idempotent: subscribing twice from the same browser is a no-op (the
 * backend upserts on endpoint).
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlB64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function currentPermission() {
  return pushSupported() ? Notification.permission : 'denied';
}

async function getOrRegisterSw() {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

/**
 * Ensure permission + subscribe. Returns the subscription object on
 * success, or throws an Error with a user-readable message.
 */
export async function ensurePushPermissionAndSubscribe() {
  if (!pushSupported()) {
    throw new Error('This browser does not support push notifications.');
  }
  const reg = await getOrRegisterSw();

  // Request permission if needed
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  // Get VAPID public key
  const keyResp = await axios.get(`${API}/push/vapid-public-key`);
  const publicKey = keyResp.data.public_key;
  if (!publicKey) throw new Error('Server has no VAPID key configured.');

  // Reuse existing subscription if it matches; else create one
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Compare applicationServerKey — if it changed, recreate.
    try {
      const existingKey = sub.options?.applicationServerKey;
      if (!existingKey) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch (_e) { /* fall through */ }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(publicKey),
    });
  }

  await axios.post(`${API}/push/subscribe`, {
    subscription: sub.toJSON(),
    user_agent: navigator.userAgent,
  });

  return sub;
}

export async function disablePush() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
  } catch (_e) { /* ignore — we still unsubscribe locally */ }
  await sub.unsubscribe();
  return true;
}
